import apiClient from '../helpers/apiClient'

export interface PublicExpert {
  id: number
  company_name: string
  description: string | null
  address: string | null
  logo: string | null
  user_id?: number
  expertise_count?: number
}

// PR-I-82: el backend aplica CamelCaseJSONRenderer global, asi que la respuesta cruda trae
// companyName/expertiseCount/userId. Los consumidores de PublicExpert siguen escritos en
// snake_case, asi que normalizamos aqui una sola vez para no repartir aliases por todos lados.
const normalizeExpert = (raw: any): PublicExpert => ({
  id: raw?.id,
  company_name: raw?.company_name ?? raw?.companyName ?? '',
  description: raw?.description ?? null,
  address: raw?.address ?? null,
  logo: raw?.logo ?? null,
  user_id: raw?.user_id ?? raw?.userId,
  expertise_count: raw?.expertise_count ?? raw?.expertiseCount ?? 0,
})

/**
 * GET /api/v1/expertise/search/experts/?keywords=&where=
 * ExpertSearchView — Partners with partner_type='Expert' AND status='active' AND at least one
 * published ExpertiseService. Search matches company_name, description, or service title/desc.
 */
export const getExperts = async (
  keywords = '',
  where = ''
): Promise<PublicExpert[]> => {
  const params: Record<string, string> = {}
  if (keywords) params.keywords = keywords
  if (where) params.where = where
  const {data} = await apiClient.get('/expertise/search/experts/', {params})
  // The endpoint returns a DRF paginated envelope; fall back to a raw array in case pagination is
  // disabled server-side or a different response shape is deployed.
  const raw = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
    ? data.results
    : []
  return raw.map(normalizeExpert)
}

export interface PaginatedExperts {
  results: PublicExpert[]
  /** URL a la siguiente pagina o null cuando ya no hay mas resultados. */
  next: string | null
  /** Total absoluto reportado por el envelope de DRF; util para "N Matches" en headers. */
  count: number
}

/**
 * GET /api/v1/expertise/search/experts/?limit=&offset= — variante paginada para el modo
 * "discovery" (sin query) que alimenta el infinite scroll. La respuesta viene con el envelope
 * estandar de DRF LimitOffsetPagination: {count, next, previous, results}. Mantenemos
 * `getExperts` para el flujo con query, donde el consumidor solo espera la lista.
 */
export const getExpertsPaginated = async (
  {limit, offset, keywords = '', where = ''}: {
    limit: number
    offset: number
    keywords?: string
    where?: string
  }
): Promise<PaginatedExperts> => {
  const params: Record<string, string | number> = {limit, offset}
  if (keywords) params.keywords = keywords
  if (where) params.where = where
  const {data} = await apiClient.get('/expertise/search/experts/', {params})
  const rawResults: any[] = Array.isArray(data?.results) ? data.results : []
  return {
    results: rawResults.map(normalizeExpert),
    next: data?.next ?? null,
    count: typeof data?.count === 'number' ? data.count : rawResults.length,
  }
}
